import { Stack, TextField } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { AdminAuditActorType, AdminAuditLogFilters } from '@/api/models';
import { fetchAllAdminTenants } from '@/api/fetchAll';
import { FilterableMultiSelect } from '@/components/form/FilterableMultiSelect';
import { FilterableSelect } from '@/components/form/FilterableSelect';
import { tenantToOption } from '@/components/form/optionMappers';
import { DateRangeFields } from '@/features/operations/filters/DateRangeFields';
import { EnumMultiSelect } from '@/features/operations/filters/EnumMultiSelect';
import { TagsField } from '@/features/operations/filters/TagsField';
import { useApiResource } from '@/hooks/useApiResource';
import { AUDIT_ACTION_CODES, AUDIT_ACTOR_TYPES } from '../domain';
import { useAuditFacet } from '../useAuditFacet';

interface AuditLogFiltersFormProps {
  filters: AdminAuditLogFilters;
  onChange: (patch: Partial<AdminAuditLogFilters>) => void;
}

/**
 * Every query parameter `GET /v1/admin/audit-log` and its `/export` sibling
 * accept, and no more: `dateFrom`, `dateTo`, `actor`, `actorType`, `action`,
 * `objectType`, `objectId`, `tenantCode`. Pagination lives on the list view.
 * `actor`/`objectType`/`objectId` are single-value contract parameters
 * (never repeatable), unlike `actorType`/`action`/`tenantCode`.
 *
 * The single-value text filters are facet-backed comboboxes
 * (`GET /v1/admin/audit-logs/facets`): the actor population follows the
 * selected actor types, and the object-id population is scoped to the
 * selected object type (the facet endpoint refuses an unscoped objectId
 * request, so that select stays disabled until a type is chosen). Actor and
 * object id remain free-text on top of their options — the facet values are
 * capped at 1000 server-side, so an absent value must still be typable. Any
 * facet failure degrades that one field to a plain text input; the tenant
 * combobox likewise degrades to the free-code chip field. Filtering never
 * blocks on a facet.
 */
export function AuditLogFiltersForm({ filters, onChange }: AuditLogFiltersFormProps) {
  const translate = useTranslate();

  const facetDates = { dateFrom: filters.dateFrom, dateTo: filters.dateTo };
  const actorFacet = useAuditFacet('actor', { ...facetDates, actorType: filters.actorType });
  const objectTypeFacet = useAuditFacet('objectType', facetDates);
  const objectIdFacet = useAuditFacet(
    'objectId',
    { ...facetDates, objectType: filters.objectType },
    Boolean(filters.objectType),
  );

  const tenants = useApiResource(() => fetchAllAdminTenants(), 'audit-filter-tenants');
  const tenantsFailed = Boolean(tenants.error);

  const asOptions = (values: string[]) => values.map((value) => ({ value, label: value }));

  // Actor options prefer the paired shape ({actor, actorDisplay}): the display
  // name is the visible label with the raw id as a mono second line, typing
  // matches both, and picking always submits the id. Actors the log never saw
  // a name for (old rows, applications) list the id alone.
  const actorOptions = actorFacet.actors
    ? actorFacet.actors.map((pair) => ({
        value: pair.actor,
        label: pair.actorDisplay ?? pair.actor,
        secondary: pair.actorDisplay ? pair.actor : undefined,
        secondaryMono: true,
      }))
    : asOptions(actorFacet.values);

  return (
    <Stack spacing={2.5}>
      <DateRangeFields
        fromLabel={translate('pay.audit.filters.dateFrom')}
        toLabel={translate('pay.audit.filters.dateTo')}
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        onChangeFrom={(dateFrom) => onChange({ dateFrom })}
        onChangeTo={(dateTo) => onChange({ dateTo })}
      />
      {actorFacet.failed ? (
        <TextField
          size="small"
          label={translate('pay.audit.filters.actor')}
          helperText={translate('pay.audit.filters.actorHelp')}
          value={filters.actor ?? ''}
          onChange={(event) => onChange({ actor: event.target.value || undefined })}
          fullWidth
        />
      ) : (
        <FilterableSelect
          freeSolo
          label={translate('pay.audit.filters.actor')}
          helperText={translate('pay.audit.filters.actorHelp')}
          options={actorOptions}
          loading={actorFacet.loading}
          value={filters.actor ?? ''}
          onChange={(actor) => onChange({ actor: actor || undefined })}
        />
      )}
      <EnumMultiSelect
        label={translate('pay.audit.filters.actorType')}
        options={AUDIT_ACTOR_TYPES.map((value) => ({ value, label: translate(`pay.audit.actorType.${value}`) }))}
        values={filters.actorType ?? []}
        onChange={(values) => onChange({ actorType: values.length > 0 ? (values as AdminAuditActorType[]) : undefined })}
      />
      <EnumMultiSelect
        label={translate('pay.audit.filters.action')}
        options={AUDIT_ACTION_CODES.map((value) => ({
          value,
          label: translate(`pay.audit.action.${value}`, { _: value }),
        }))}
        values={filters.action ?? []}
        onChange={(values) => onChange({ action: values.length > 0 ? values : undefined })}
      />
      {objectTypeFacet.failed ? (
        <TextField
          size="small"
          label={translate('pay.audit.filters.objectType')}
          helperText={translate('pay.audit.filters.objectTypeHelp')}
          value={filters.objectType ?? ''}
          onChange={(event) => onChange({ objectType: event.target.value || undefined, objectId: undefined })}
          fullWidth
        />
      ) : (
        <FilterableSelect
          label={translate('pay.audit.filters.objectType')}
          helperText={translate('pay.audit.filters.objectTypeHelp')}
          options={objectTypeFacet.values.map((value) => ({
            value,
            label: translate(`pay.audit.objectType.${value}`, { _: value }),
            secondary: value,
          }))}
          loading={objectTypeFacet.loading}
          value={filters.objectType ?? ''}
          // A different (or cleared) object type invalidates the current
          // object-id narrowing — its population belongs to the previous type.
          onChange={(objectType) => onChange({ objectType: objectType || undefined, objectId: undefined })}
        />
      )}
      {objectIdFacet.failed ? (
        <TextField
          size="small"
          label={translate('pay.audit.filters.objectId')}
          helperText={translate('pay.audit.filters.objectIdHelp')}
          value={filters.objectId ?? ''}
          onChange={(event) => onChange({ objectId: event.target.value || undefined })}
          fullWidth
        />
      ) : (
        <FilterableSelect
          freeSolo
          label={translate('pay.audit.filters.objectId')}
          helperText={
            filters.objectType
              ? translate('pay.audit.filters.objectIdHelp')
              : translate('pay.audit.filters.objectIdNeedsObjectType')
          }
          options={asOptions(objectIdFacet.values)}
          loading={objectIdFacet.loading}
          disabled={!filters.objectType}
          value={filters.objectId ?? ''}
          onChange={(objectId) => onChange({ objectId: objectId || undefined })}
        />
      )}
      {tenantsFailed ? (
        <TagsField
          label={translate('pay.audit.filters.tenantCode')}
          helperText={translate('pay.audit.filters.tenantCodeHelp')}
          values={filters.tenantCode ?? []}
          onChange={(values) => onChange({ tenantCode: values.length > 0 ? values : undefined })}
        />
      ) : (
        <FilterableMultiSelect
          freeSolo
          label={translate('pay.audit.filters.tenantCode')}
          helperText={translate('pay.audit.filters.tenantCodeHelp')}
          options={(tenants.data ?? []).map(tenantToOption)}
          loading={tenants.loading}
          values={filters.tenantCode ?? []}
          onChange={(values) => onChange({ tenantCode: values.length > 0 ? values : undefined })}
        />
      )}
    </Stack>
  );
}
